import { useAuth } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { AuthService, type IAuthService } from '../../services/auth/auth.service';
import {
  getMembershipsRefetchInterval,
  shouldRetryMembershipsQuery,
} from './use-memberships.helpers';

const authQueryKeys = {
  all: ['auth'] as const,
  memberships: () => ['auth', 'memberships'] as const,
};

export function useMemberships() {
  const authService = useInjection<IAuthService>(AuthService);
  const { isSignedIn } = useAuth();

  const { data, isLoading, isPending, isError, dataUpdatedAt, errorUpdatedAt } = useQuery({
    queryKey: authQueryKeys.memberships(),
    queryFn: () => authService.getMemberships(),
    enabled: !!isSignedIn,
    refetchInterval: (query) => getMembershipsRefetchInterval(query.state.data),
    retry: shouldRetryMembershipsQuery,
  });

  const memberships = data?.memberships ?? [];

  return {
    memberships,
    isPlatformAdmin: data?.isPlatformAdmin ?? false,
    consultantAccessBlocked: data?.consultantAccessBlocked ?? false,
    isLoading,
    isPending,
    isError,
    /** `Date.now()` of the last response, success or error (0 before the first one). */
    respondedAt: Math.max(dataUpdatedAt, errorUpdatedAt),
  };
}

export { authQueryKeys };
